import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { t } from 'i18next';
import useAsyncEffect from 'use-async-effect';

import { InfluencerCampaignType } from '@anyx/common-graphql';


import { ValueEditorProps, useValueEditor } from '../../condition-builder';
import { Option } from '../../condition-builder/types';
import { GroupFilterGroup, GroupFilterSelector } from '../../group-filter-selector';
import { GroupFilterOption } from '../../group-filter-selector';

type UsePrimitiveValueProps<Option> = {
  value?: string;
  options?: Option[];
};

export function usePrimitiveValue({
  value,
  options = [],
}: UsePrimitiveValueProps<GroupFilterOption>): GroupFilterOption | undefined {
  return useMemo(() => {
    if (value) {
      return options.find((option) => (option as { value?: unknown }).value === value);
    }

    return undefined;
  }, [value, options]);
}

const campaignFilterGroupBuilder = (data: Option[]): GroupFilterGroup[] => {
  const marketplaceOptions: Option[] = [];
  const engagementOptions: Option[] = [];
  const eCommerceAffiliatesOptions: Option[] = [];
  const tiktokSpecialOptions: Option[] = [];
  data.forEach((option) => {
    if (option['type'] === InfluencerCampaignType.CAMPAIGN_TYPE_MARKETPLACE) {
      marketplaceOptions.push(option);
    }

    if (option['type'] === InfluencerCampaignType.CAMPAIGN_TYPE_ENGAGEMENT) {
      engagementOptions.push(option);
    }

    if (option['type'] === InfluencerCampaignType.CAMPAIGN_TYPE_AUTO_MANAGED) {
      eCommerceAffiliatesOptions.push(option);
    }

    if (option['type'] === InfluencerCampaignType.CAMPAIGN_TYPE_TIKTOK_SPECIAL) {
      tiktokSpecialOptions.push(option);
    }

    return;
  });

  return [
    {
      label: t('anychat.broadcast.conditions.influencer.campaigns.campaignType', {
        type: InfluencerCampaignType.CAMPAIGN_TYPE_MARKETPLACE,
        ns: 'anychat',
      }),
      options: marketplaceOptions,
    },
    {
      label: t('anychat.broadcast.conditions.influencer.campaigns.campaignType', {
        type: InfluencerCampaignType.CAMPAIGN_TYPE_ENGAGEMENT,
        ns: 'anychat',
      }),
      options: engagementOptions,
    },
    {
      label: t('anychat.broadcast.conditions.influencer.campaigns.campaignType', {
        type: InfluencerCampaignType.CAMPAIGN_TYPE_AUTO_MANAGED,
        ns: 'anychat',
      }),
      options: eCommerceAffiliatesOptions,
    },
    {
      label: t('anychat.broadcast.conditions.influencer.campaigns.campaignType', {
        type: InfluencerCampaignType.CAMPAIGN_TYPE_TIKTOK_SPECIAL,
        ns: 'anychat',
      }),
      options: tiktokSpecialOptions,
    },
  ];
};

export const CampaignValueEditor = (props: ValueEditorProps) => {
  const { t } = useTranslation('shared');
  const [options, setOptions] = useState<Option[]>([]);

  const { baseProps, getErrorMessage } = useValueEditor(props);

  const primitiveValue = props.values?.[0];
  const error = getErrorMessage();

  const getOptions = props.field?.onLoadOptions?.();
  const getOptionsWithInitialValue = props.field?.onLoadOptions?.(primitiveValue);

  const InfluencerCampaignLoadOption = async (keyword?: string) => {
    const data = await getOptions?.(keyword);

    if (!data) return [];
    return campaignFilterGroupBuilder(data);
  };

  const value = usePrimitiveValue({
    value: primitiveValue,
    options,
  });

  useAsyncEffect(async () => {
    const data = await getOptionsWithInitialValue?.();
    setOptions(data ?? []);
  }, []);

  return (
    <GroupFilterSelector
      {...baseProps}
      error={error}
      placeholder={t('shared.entity.campaign', { count: 1 })}
      loadOptions={InfluencerCampaignLoadOption}
      value={value}
      defaultOptions={campaignFilterGroupBuilder([...options])}
      onChange={(option) => props.onChange?.([option?.value || ''])}
      menuPortalTarget={document.body}
      classNames={{
        menuList: () => 'gap-y-4 flex flex-col m-2 max-h-64!',
        menu: () => 'min-w-76',
      }}
      disabled={props.disabled}
    />
  );
};
